import { useEffect, useCallback } from 'react';
import { useWebSocketContext, WebSocketMessage } from '../contexts/WebSocketContext';

export type { WebSocketMessage, ConnectionStatus } from '../contexts/WebSocketContext';

export interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void;
  destination?: string;
  enabled?: boolean;
}

/**
 * useWebSocket Hook
 * 
 * Optimized hook that uses a shared WebSocket connection via WebSocketContext.
 * Prevents multiple concurrent connections and "Too Many Requests" (429) errors.
 */
export function useWebSocket(options: UseWebSocketOptions = {}) {
  // Extract options if provided (legacy pattern)
  const { onMessage, destination, enabled = true } = options;
  const { status, isConnected, subscribe, sendMessage } = useWebSocketContext();

  /**
   * Support for useEffect style callback registration via options
   */
  useEffect(() => {
    if (enabled && destination && onMessage) {
      const unsubscribe = subscribe(destination, onMessage);
      return () => unsubscribe();
    }
  }, [enabled, destination, onMessage, subscribe]);

  /**
   * Compatibility methods (shared connection handles these)
   */
  const connect = useCallback(() => {
    // Managed by Provider
  }, []);

  const disconnect = useCallback(() => {
    // Managed by Provider
  }, []);

  const unsubscribe = useCallback((_dest: string) => {
    // In the new pattern, you should use the function returned by subscribe()
    console.warn('⚠️ unsubscribe(destination) is deprecated. Use the function returned by subscribe() instead.');
  }, []);

  return {
    status,
    isConnected,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    sendMessage
  };
}
